import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/admin/ui";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, CreditCard, Mail, Building2, Banknote, XCircle, AlertTriangle, Loader2 } from "lucide-react";

function TestEmail() {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!/^\S+@\S+\.\S+$/.test(to)) { toast.error("Enter a valid email address"); return; }
    setBusy(true);
    try {
      const r = await api.post("/settings/test-email", { to });
      toast.success(`Test email sent via ${r.data.provider}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send test email");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 flex gap-2" data-testid="settings-test-email">
      <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@example.com"
        data-testid="settings-test-email-input" className="h-9" />
      <Button onClick={send} disabled={busy} data-testid="settings-test-email-button"
        className="h-9 bg-[#0D3B2D] hover:bg-[#071A13] text-white rounded-lg shrink-0">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send test"}
      </Button>
    </div>
  );
}

function MfaCard() {
  const [st, setSt] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const load = () => api.get("/auth/mfa/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const begin = () => api.post("/auth/mfa/setup").then((r) => setSetup(r.data)).catch(() => toast.error("Could not start setup"));
  const verify = () => api.post("/auth/mfa/verify", { code }).then(() => { setSetup(null); setCode(""); load(); toast.success("Two-factor authentication enabled"); }).catch((e) => toast.error(e.response?.data?.detail || "Invalid code"));
  const disable = () => api.post("/auth/mfa/disable", { code }).then(() => { setCode(""); load(); toast.success("Two-factor authentication disabled"); }).catch((e) => toast.error(e.response?.data?.detail || "Invalid code"));
  return (
    <div className="bg-white border border-[#E2EBF0] rounded-xl p-5 mt-6" data-testid="mfa-card">
      <div className="flex items-center gap-2 mb-3"><Banknote className="h-4 w-4 text-[#10B981]" /><h2 className="font-heading font-semibold text-[#0D3B2D]">Two-Factor Authentication (TOTP)</h2></div>
      <p className="text-sm text-[#4A635A] mb-4">{st?.enabled ? "Enabled — you'll be asked for a 6-digit code at login." : "Protect your admin account with an authenticator app. You can enroll safely before it becomes required."}</p>
      {!st?.enabled && !setup && <Button onClick={begin} data-testid="mfa-setup-btn" className="bg-[#0D3B2D] text-white rounded-lg">Set up 2FA</Button>}
      {setup && (
        <div className="space-y-3" data-testid="mfa-setup-panel">
          <p className="text-xs text-[#4A635A]">Scan or paste this secret in your authenticator app, then enter the current code.</p>
          <div className="font-mono text-sm bg-[#F4FFF9] border border-[#BCECD8] rounded p-2 break-all" data-testid="mfa-secret">{setup.secret}</div>
          <div className="text-xs text-[#4A635A]">Recovery codes (store safely): <span className="font-mono">{setup.recovery_codes.join("  ")}</span></div>
          <div className="flex gap-2 items-center">
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" data-testid="mfa-verify-code" className="w-32 font-mono" />
            <Button onClick={verify} data-testid="mfa-verify-btn" className="bg-[#67F2B3] text-[#071A13] rounded-lg">Verify & Enable</Button>
          </div>
        </div>
      )}
      {st?.enabled && (
        <div className="flex gap-2 items-center">
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="code to disable" data-testid="mfa-disable-code" className="w-40 font-mono" />
          <Button onClick={disable} variant="outline" data-testid="mfa-disable-btn" className="border-red-200 text-red-600 rounded-lg">Disable 2FA</Button>
        </div>
      )}
    </div>
  );
}

function OpsPanel() {
  const [dls, setDls] = useState([]);
  const [terms, setTerms] = useState([]);
  const load = () => {
    api.get("/download-requests").then((r) => setDls(r.data.filter((x) => !["downloaded", "revoked", "rejected"].includes(x.status)))).catch(() => {});
    api.get("/terminations").then((r) => setTerms(r.data.filter((x) => x.status !== "case_closed"))).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const approve = (id) => { const p = prompt("Release price USD (0 = free):", "0"); if (p === null) return; api.post(`/download-requests/${id}/approve`, { price: parseFloat(p) || 0, requires_terms: (parseFloat(p) || 0) > 0 }).then(() => { toast.success("Approved"); load(); }).catch(() => toast.error("Failed")); };
  const reject = (id) => api.post(`/download-requests/${id}/reject`, { reason: "Not eligible" }).then(() => { toast.success("Rejected"); load(); });
  const setTerm = (id) => { const s = prompt("New status (offboarding_in_progress, files_ready, transfer_completed, service_terminated, case_closed):"); if (!s) return; api.post(`/terminations/${id}/status`, { version: s }).then(() => { toast.success("Updated"); load(); }).catch((e) => toast.error(e.response?.data?.detail || "Invalid")); };
  return (
    <div className="bg-white border border-[#E2EBF0] rounded-xl p-5 mt-6" data-testid="ops-panel">
      <h2 className="font-heading font-semibold text-[#0D3B2D] mb-3">Download Requests &amp; Offboarding</h2>
      <div className="text-xs uppercase tracking-wide text-[#4A635A] mb-2">Download requests</div>
      {dls.length === 0 ? <p className="text-sm text-[#4A635A]">No open requests.</p> : (
        <ul className="space-y-2 mb-4" data-testid="ops-downloads">
          {dls.map((r) => (
            <li key={r.request_id} className="flex items-center justify-between gap-2 text-sm border border-[#E2EBF0] rounded-lg px-3 py-2">
              <span className="min-w-0">{r.client_name} · {r.file_name} · <span className="font-mono">{r.status}</span></span>
              <span className="flex gap-2 shrink-0">
                <button onClick={() => approve(r.request_id)} data-testid={`approve-dl-${r.request_id}`} className="text-[#10B981] underline text-xs">Approve</button>
                <button onClick={() => reject(r.request_id)} className="text-red-600 underline text-xs">Reject</button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="text-xs uppercase tracking-wide text-[#4A635A] mb-2">Terminations</div>
      {terms.length === 0 ? <p className="text-sm text-[#4A635A]">No open terminations.</p> : (
        <ul className="space-y-2" data-testid="ops-terminations">
          {terms.map((t) => (
            <li key={t.term_id} className="flex items-center justify-between gap-2 text-sm border border-[#E2EBF0] rounded-lg px-3 py-2">
              <span className="min-w-0">{t.client_name} · {t.service_name} · <span className="font-mono">{t.status}</span></span>
              <button onClick={() => setTerm(t.term_id)} data-testid={`term-status-${t.term_id}`} className="text-[#0D3B2D] underline text-xs shrink-0">Set status</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PayoutReadiness() {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/stripe/payout-readiness").then((r) => setP(r.data)).catch(() => setP({ error: "Unavailable" })).finally(() => setLoading(false));
  }, []);

  const Flag = ({ ok, label }) => (
    <div className="flex items-center gap-2 text-sm" data-testid={`payout-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      {ok ? <CheckCircle2 className="h-4 w-4 text-[#10B981]" /> : <XCircle className="h-4 w-4 text-amber-500" />}
      <span className="text-[#0D3B2D]">{label}</span>
    </div>
  );

  return (
    <div className="bg-white border border-[#E2EBF0] rounded-xl p-5 mt-6" data-testid="payout-readiness-card">
      <div className="flex items-center gap-2 mb-4"><Banknote className="h-4 w-4 text-[#10B981]" /><h2 className="font-heading font-semibold text-[#0D3B2D]">Stripe Payout Readiness</h2></div>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#4A635A]"><Loader2 className="h-4 w-4 animate-spin" /> Checking account…</div>
      ) : p?.error ? (
        <p className="text-sm text-amber-600 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {p.error}</p>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            <Flag ok={p.charges_enabled} label="Charges enabled" />
            <Flag ok={p.payouts_enabled} label="Payouts enabled" />
            <Flag ok={p.details_submitted} label="Account details submitted" />
            <Flag ok={!p.requirements_due || p.requirements_due.length === 0} label="No outstanding requirements" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm text-[#4A635A]">
            <div><span className="text-[#0D3B2D] font-medium">Mode:</span> <span className="font-mono uppercase">{p.mode}</span></div>
            {p.account_id && <div><span className="text-[#0D3B2D] font-medium">Account:</span> <span className="font-mono">{p.account_id}</span></div>}
            {p.payout_schedule && <div><span className="text-[#0D3B2D] font-medium">Payout schedule:</span> {p.payout_schedule}</div>}
            {p.external_account_last4 && <div><span className="text-[#0D3B2D] font-medium">Destination:</span> {p.external_account_bank} ••••{p.external_account_last4}</div>}
          </div>
          {(p.available?.length > 0 || p.pending?.length > 0) && (
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <div className="bg-[#F4FFF9] border border-[#BCECD8] rounded-lg p-3">
                <div className="text-[11px] uppercase tracking-wide text-[#10B981]">Available</div>
                <div className="font-mono text-[#0D3B2D]">{(p.available || []).map((b) => `${b.amount} ${b.currency}`).join(", ") || "—"}</div>
              </div>
              <div className="bg-[#F8FAF9] border border-[#E2EBF0] rounded-lg p-3">
                <div className="text-[11px] uppercase tracking-wide text-[#4A635A]">Pending</div>
                <div className="font-mono text-[#0D3B2D]">{(p.pending || []).map((b) => `${b.amount} ${b.currency}`).join(", ") || "—"}</div>
              </div>
            </div>
          )}
          {p.requirements_due?.length > 0 && (
            <p className="text-xs text-amber-600 mt-4" data-testid="payout-requirements">Outstanding: {p.requirements_due.join(", ")}</p>
          )}
          <p className="text-xs text-[#4A635A] mt-4">Read-only overview. ECOREXS never changes your bank details or moves money from here. Complete any outstanding items in your Stripe Dashboard before going live.</p>
        </>
      )}
    </div>
  );
}

function DownloadTermsCard() {
  const [t, setT] = useState(null);
  useEffect(() => { api.get("/billing/download-terms").then((r) => setT(r.data)).catch(() => {}); }, []);
  const save = () => api.put("/settings/download-terms", {
    version: t.version, text_en: t.text_en, text_es: t.text_es, requires_legal_review: !!t.requires_legal_review,
  }).then(() => toast.success("Download transfer terms saved")).catch((e) => toast.error(e.response?.data?.detail || "Could not save"));
  if (!t) return null;
  return (
    <div className="bg-white border border-[#E2EBF0] rounded-xl p-5 mt-6" data-testid="download-terms-card">
      <div className="flex items-center gap-2 mb-3"><AlertTriangle className="h-4 w-4 text-[#10B981]" /><h2 className="font-heading font-semibold text-[#0D3B2D]">Download Transfer Terms (EN / ES)</h2></div>
      <div className="grid gap-4">
        <div className="grid sm:grid-cols-2 gap-4 items-end">
          <div><Label>Version label</Label><Input value={t.version || ""} onChange={(e) => setT({ ...t, version: e.target.value })} data-testid="terms-version-input" className="bg-white border-[#E2EBF0] font-mono" /></div>
          <label className="flex items-center gap-2 text-sm text-[#0D3B2D] pb-2"><input type="checkbox" checked={!!t.requires_legal_review} onChange={(e) => setT({ ...t, requires_legal_review: e.target.checked })} data-testid="terms-legal-check" /> Pending legal review</label>
        </div>
        <div><Label>Terms (English)</Label><Textarea value={t.text_en || ""} onChange={(e) => setT({ ...t, text_en: e.target.value })} rows={5} data-testid="terms-en-input" className="bg-white border-[#E2EBF0]" /></div>
        <div><Label>Terms (Español)</Label><Textarea value={t.text_es || ""} onChange={(e) => setT({ ...t, text_es: e.target.value })} rows={5} data-testid="terms-es-input" className="bg-white border-[#E2EBF0]" /></div>
      </div>
      <Button onClick={save} data-testid="terms-save-btn" className="mt-4 bg-[#0D3B2D] hover:bg-[#071A13] text-white rounded-lg">Save Transfer Terms</Button>
      <p className="text-xs text-[#4A635A] mt-2">Shown to clients before they accept a file transfer. Flag for legal review before commercial use.</p>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const isSuper = user.role === "super_admin";
  const [data, setData] = useState(null);
  const [biz, setBiz] = useState({});

  const load = () => api.get("/settings").then((r) => { setData(r.data); setBiz(r.data.business || {}); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.put("/settings/business", biz); toast.success("Settings saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  const f = (k) => ({ value: biz[k] || "", onChange: (e) => setBiz({ ...biz, [k]: e.target.value }), className: "bg-white border-[#E2EBF0]" });

  if (!data) return null;

  return (
    <div className="animate-fade-up max-w-3xl">
      <PageHeader title="Settings" subtitle="Business, billing and legal configuration" />

      {/* Connections */}
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-[#E2EBF0] rounded-xl p-5" data-testid="settings-stripe-card">
          <div className="flex items-center gap-2 mb-2"><CreditCard className="h-4 w-4 text-[#10B981]" /><span className="font-heading font-semibold text-[#0D3B2D]">Stripe</span></div>
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle2 className="h-4 w-4 text-[#10B981]" />
            <span className="text-[#0D3B2D]">Connected · <span className="font-mono uppercase">{data.stripe.mode}</span> mode</span>
          </div>
          <p className="text-xs text-[#4A635A] mt-2">Products and prices are created automatically — no manual Stripe setup needed.</p>
        </div>
        <div className="bg-white border border-[#E2EBF0] rounded-xl p-5" data-testid="settings-email-card">
          <div className="flex items-center gap-2 mb-2"><Mail className="h-4 w-4 text-[#10B981]" /><span className="font-heading font-semibold text-[#0D3B2D]">Email</span></div>
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle2 className={`h-4 w-4 ${data.email.configured ? "text-[#10B981]" : "text-amber-500"}`} />
            <span className="text-[#0D3B2D]">{data.email.configured ? "Transactional email active" : "Not configured"}</span>
          </div>
          <p className="text-xs text-[#4A635A] mt-2">Sender: {data.email.from_name}{data.email.provider && data.email.provider !== "none" ? ` · via ${data.email.provider}` : ""}</p>
          {user?.role === "super_admin" && data.email.configured && <TestEmail />}
        </div>
        {user?.role === "super_admin" && (
          <a href="/admin/test-data" data-testid="settings-test-data-link"
            className="block mt-4 text-sm text-[#10B981] hover:underline">Test Data Cleanup →</a>
        )}
      </div>

      {/* Business + legal */}
      <div className="bg-white border border-[#E2EBF0] rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4"><Building2 className="h-4 w-4 text-[#10B981]" /><h2 className="font-heading font-semibold text-[#0D3B2D]">Business & Legal</h2></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Business Name</Label><Input {...f("business_name")} data-testid="settings-business-name" placeholder="ECOREXS" /></div>
          <div><Label>Contact Email</Label><Input {...f("contact_email")} data-testid="settings-contact-email" /></div>
          <div><Label>Contact Phone</Label><Input {...f("contact_phone")} data-testid="settings-contact-phone" /></div>
          <div><Label>Merchant Info</Label><Input {...f("merchant_info")} data-testid="settings-merchant" placeholder="Responsible merchant / payment recipient" /></div>
        </div>
        <div className="grid gap-4 mt-4">
          <div><Label>Billing & Cancellation Policy</Label><Textarea {...f("cancellation_policy")} rows={3} data-testid="settings-cancellation" /></div>
          <div><Label>Terms of Service</Label><Textarea {...f("legal_terms")} rows={3} data-testid="settings-terms" /></div>
          <div><Label>Privacy Policy</Label><Textarea {...f("privacy_policy")} rows={3} data-testid="settings-privacy" /></div>
        </div>
        {isSuper ? (
          <Button onClick={save} data-testid="settings-save-button" className="mt-4 bg-[#0D3B2D] hover:bg-[#071A13] text-white rounded-lg">Save Settings</Button>
        ) : (
          <p className="text-xs text-[#4A635A] mt-4">Only the Super Admin can edit these settings.</p>
        )}
      </div>

      {isSuper && <MfaCard />}
      {isSuper && <OpsPanel />}
      {isSuper && <DownloadTermsCard />}
      {isSuper && <PayoutReadiness />}
    </div>
  );
}
